import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AssignmentType,
  OrganizationMember,
  Site,
  SiteAssignmentWithMember,
  getUserIdentifier,
  organizationsApi,
  sitesApi,
} from '../lib/organizations';
import { getInspections, InspectionCollaborator } from '../lib/inspections';
import { isOrganizationMember, memberDisplayName } from '../lib/members';

export interface ParticipantState {
  memberId: string;
  userId: string;
  name: string;
  identifier: string;
  checked: boolean;
  role: 'PRIMARY' | 'SECONDARY';
  assignmentType?: AssignmentType;
}

export interface ParticipantPayload {
  participantUserIds: string[];
  primaryParticipantUserId?: string;
}

export type InitialParticipantsSource =
  | { kind: 'previous-inspection' }
  | { kind: 'explicit'; participants: InspectionCollaborator[]; inspectorId?: string | null }
  | { kind: 'first-inspection' };

interface UseParticipantsStateOptions {
  companyId: string | undefined;
  siteId: string | undefined;
  /**
   * 초기값 결정 방식.
   * - previous-inspection: 이 현장의 직전 점검 참가자 기준 (없으면 first-inspection fallback)
   * - explicit: 주어진 참가자 목록 그대로 사용
   * - first-inspection: 현장 배정 멤버 전원 ON (PRIMARY는 현장 PRIMARY 또는 첫 번째)
   */
  initialSource: InitialParticipantsSource;
  /** 활성화 여부 (false면 멤버/현장 로드 스킵) */
  enabled?: boolean;
}

interface UseParticipantsStateResult {
  /** 현장에 배정된 조직 멤버 (로드 완료 시) */
  members: OrganizationMember[];
  /** 선택 상태가 적용된 참가자 목록 */
  participants: ParticipantState[];
  /** 선택된 현장 (있으면) */
  site: Site | null;
  /** 현장에 아직 배정되지 않은 조직 멤버 (추가 모드용) */
  unassignedMembers: OrganizationMember[];
  /** 체크박스 토글 (PRIMARY는 보호됨) */
  toggleMember: (memberId: string) => void;
  /** PRIMARY 지정 (체크도 ON) */
  setPrimary: (memberId: string) => void;
  /**
   * 외부에서 새 참가자(추가된 멤버) 주입
   * - 이미 있으면 체크 ON, role=SECONDARY로 업데이트
   * - 없으면 참가자 목록에 추가
   */
  addParticipant: (member: OrganizationMember) => void;
  /** 현재 체크된 참가자로 API payload 생성 */
  getPayload: () => ParticipantPayload;
  /** 로딩 상태 */
  isLoading: boolean;
  /**
   * 초기 참가자 목록 빌드가 한 번이라도 완료됐는가. `isLoading`이 false가 되는 시점(멤버/현장
   * 로드 완료)에는 participants가 아직 빈 배열이고, 그 다음 커밋에서 빌드 effect가 최종 목록을
   * 채운다. dirty 기준점은 이 플래그가 true가 된 뒤에 잡아야 빈 목록을 기준으로 오판하지 않는다.
   */
  isInitialized: boolean;
  /** validation 에러 메시지 (없으면 null) */
  validationError: string | null;
  /** 체크된 참가자 수 */
  checkedCount: number;
  /** 현장/멤버 재로드 */
  refetchSite: () => Promise<void>;
}

/**
 * 점검 참가자 선택 상태 관리 훅.
 *
 * CreateInspectionModal / ParticipantsEditModal 양쪽에서 공유.
 * - 현장 배정 멤버 로드
 * - 체크/해제, PRIMARY 단일 지정
 * - 최초 점검(전체 ON) / 이후 점검(이전 참여자만 ON) 기본값 처리
 * - PRIMARY 정확히 1명 보장
 */
export function useParticipantsState({
  companyId,
  siteId,
  initialSource,
  enabled = true,
}: UseParticipantsStateOptions): UseParticipantsStateResult {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [participants, setParticipants] = useState<ParticipantState[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // 빌드 effect가 최종 목록을 채운 뒤 true. 비활성(모달 닫힘)에서는 false로 되돌려 재오픈 시
  // 다시 초기화되게 한다.
  const [isInitialized, setIsInitialized] = useState(false);

  // initialSource를 ref에 보관 (객체 리터럴이 매 렌더마다 바뀌어도 effect는 1회만)
  const initialSourceRef = useRef(initialSource);
  initialSourceRef.current = initialSource;

  const site = useMemo<Site | null>(() => {
    if (!siteId) return null;
    return sites.find((s) => s.id === siteId) ?? null;
  }, [siteId, sites]);

  // isOrganizationMember를 지나지 않으면 외부 인원(countsAsMember=false — 현장 담당자·외부 결재자용
  // CompanyMember)이 "조직 멤버 추가" 후보로 올라온다. 서버 assignMember가 비구성원을 400으로
  // 막으므로 고를 수도 없는 항목이다. `useSiteDetailState`의 같은 계산과 동형으로 유지할 것.
  const unassignedMembers = useMemo<OrganizationMember[]>(() => {
    const organizationMembers = members.filter(isOrganizationMember);
    if (!site?.assignments) return organizationMembers;
    const assignedIds = new Set(site.assignments.map((a) => a.memberId));
    return organizationMembers.filter((m) => !assignedIds.has(m.id));
  }, [site, members]);

  // 조직 멤버 + 현장 목록 로드
  useEffect(() => {
    if (!enabled || !companyId) return;
    let cancelled = false;
    setIsLoading(true);
    Promise.all([organizationsApi.getMembers(companyId), sitesApi.findAll(companyId)])
      .then(([m, s]) => {
        if (cancelled) return;
        setMembers(m);
        setSites(s);
      })
      .catch(() => {
        // 로드 실패는 조용히 무시 (호출부에서 개별 처리)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, companyId]);

  // 비활성(모달 닫힘) 동안 초기화 플래그를 내려, 재오픈 시 새 현장 기준으로 다시 잡히게 한다.
  useEffect(() => {
    if (!enabled) setIsInitialized(false);
  }, [enabled]);

  const refetchSite = useCallback(async () => {
    if (!companyId) return;
    const nextSites = await sitesApi.findAll(companyId);
    setSites(nextSites);
  }, [companyId]);

  // 초기 참가자 상태 빌드
  useEffect(() => {
    if (!enabled) return;
    if (!siteId || !companyId) {
      setParticipants([]);
      setIsInitialized(true);
      return;
    }
    if (!site) return;

    const siteAssignments: SiteAssignmentWithMember[] = (site.assignments ?? []).filter((a) =>
      members.some((m) => m.id === a.memberId),
    );
    if (siteAssignments.length === 0) {
      setParticipants([]);
      setIsInitialized(true);
      return;
    }

    const apply = (
      prevParticipants: InspectionCollaborator[] | undefined,
      prevInspectorId: string | null | undefined,
    ) => {
      // 활성 참여자만 (leftAt/excludedAt null)
      const activePrev = prevParticipants?.filter((p) => !p.leftAt && !p.excludedAt);
      const prevParticipantUserIds = new Set(activePrev?.map((p) => p.userId) ?? []);
      const prevPrimaryUserId =
        activePrev?.find((p) => p.role === 'PRIMARY')?.userId ?? prevInspectorId ?? undefined;
      const isFirstInspection = !prevParticipants && !prevInspectorId;

      const built: ParticipantState[] = siteAssignments.flatMap((assignment) => {
        const member = members.find((m) => m.id === assignment.memberId);
        if (!member) return [];
        const identifier = getUserIdentifier(member.user?.auths ?? []);
        const memberUserId = member.user?.id;
        // user 없는 멤버(연동 안 된 멤버)는 참여자 목록에서 제외
        if (!memberUserId) return [];
        const isPrevPrimary = memberUserId === prevPrimaryUserId;
        const isPrevParticipant = prevParticipantUserIds.has(memberUserId);
        const isSitePrimary = assignment.assignmentType === 'PRIMARY';

        const checked = isFirstInspection ? true : isPrevParticipant;

        let role: 'PRIMARY' | 'SECONDARY' = 'SECONDARY';
        if (prevPrimaryUserId) {
          if (isPrevPrimary) role = 'PRIMARY';
        } else if (isSitePrimary) {
          role = 'PRIMARY';
        }

        return [
          {
            memberId: assignment.memberId,
            userId: memberUserId,
            name: memberDisplayName(member),
            identifier,
            checked,
            role,
            assignmentType: assignment.assignmentType as AssignmentType | undefined,
          },
        ];
      });

      const hasPrimary = built.some((p) => p.role === 'PRIMARY');
      if (!hasPrimary && built.length > 0) {
        built[0].role = 'PRIMARY';
      }

      const finalized = built.map((p) => (p.role === 'PRIMARY' ? { ...p, checked: true } : p));
      setParticipants(finalized);
      setIsInitialized(true);
    };

    const src = initialSourceRef.current;
    if (src.kind === 'explicit') {
      apply(src.participants, src.inspectorId ?? undefined);
      return;
    }

    if (src.kind === 'first-inspection') {
      apply(undefined, undefined);
      return;
    }

    // previous-inspection: 이 현장의 직전 점검 참여자 조회
    let cancelled = false;
    getInspections(companyId, { siteId, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const last = res.data?.[0];
        apply(last?.collaborators, last?.inspectorId);
      })
      .catch(() => {
        if (cancelled) return;
        apply(undefined, undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, siteId, companyId, site, members]);

  const toggleMember = useCallback((memberId: string) => {
    setParticipants((prev) =>
      prev.map((p) => {
        if (p.memberId !== memberId) return p;
        if (p.role === 'PRIMARY') return p;
        return { ...p, checked: !p.checked };
      }),
    );
  }, []);

  const setPrimary = useCallback((memberId: string) => {
    setParticipants((prev) =>
      prev.map((p) => {
        if (p.memberId === memberId) {
          return { ...p, role: 'PRIMARY', checked: true };
        }
        if (p.role === 'PRIMARY') {
          return { ...p, role: 'SECONDARY' };
        }
        return p;
      }),
    );
  }, []);

  const addParticipant = useCallback((member: OrganizationMember) => {
    const memberUserId = member.user?.id;
    // user 없는 멤버(연동 안 된 멤버)는 추가 불가
    if (!memberUserId) return;
    const identifier = getUserIdentifier(member.user?.auths ?? []);
    setParticipants((prev) => {
      if (prev.some((p) => p.memberId === member.id)) {
        return prev.map((p) =>
          p.memberId === member.id ? { ...p, checked: true } : p,
        );
      }
      return [
        ...prev,
        {
          memberId: member.id,
          userId: memberUserId,
          name: memberDisplayName(member),
          identifier,
          checked: true,
          role: 'SECONDARY',
          assignmentType: 'SECONDARY',
        },
      ];
    });
  }, []);

  const getPayload = useCallback((): ParticipantPayload => {
    const checked = participants.filter((p) => p.checked);
    const primary = checked.find((p) => p.role === 'PRIMARY');
    return {
      participantUserIds: checked.map((p) => p.userId),
      primaryParticipantUserId: primary?.userId,
    };
  }, [participants]);

  const checkedCount = useMemo(() => participants.filter((p) => p.checked).length, [participants]);

  const validationError = useMemo<string | null>(() => {
    if (checkedCount === 0) return '최소 1명의 참가자를 선택해주세요';
    const primaryCount = participants.filter((p) => p.checked && p.role === 'PRIMARY').length;
    if (primaryCount === 0) return '주담당자를 지정해주세요';
    if (primaryCount > 1) return '주담당자는 1명만 지정할 수 있습니다';
    return null;
  }, [participants, checkedCount]);

  return {
    members,
    participants,
    site,
    unassignedMembers,
    toggleMember,
    setPrimary,
    addParticipant,
    getPayload,
    isLoading,
    isInitialized,
    validationError,
    checkedCount,
    refetchSite,
  };
}
